import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 200;
  width: 100%;
  padding: 1rem 1.5rem;
  background: rgba(15, 23, 42, 0.9);
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  backdrop-filter: blur(8px);

  @media (max-width: 900px) {
    padding: 0.75rem;
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    gap: 0.6rem;
  }
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-shrink: 0;

  @media (max-width: 900px) {
    min-width: 0;
  }
`;

const Brand = styled(NavLink)`
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.01em;

  @media (max-width: 900px) {
    font-size: 0.92rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.6);
  color: #e2e8f0;
  border-radius: 0.65rem;
  padding: 0.42rem 0.62rem;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;

  @media (max-width: 900px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const CenterNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
  justify-content: center;
  flex: 1;

  @media (max-width: 900px) {
    order: 3;
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    flex: 0 0 100%;
    justify-content: flex-start;
    flex-wrap: nowrap;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    margin-top: 0.15rem;
    border: 1px solid rgba(148, 163, 184, 0.25);
    border-radius: 0.85rem;
    background: rgba(15, 23, 42, 0.86);
    padding: 0.65rem;
  }
`;

const CenterLink = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  color: #cbd5e1;
  font-size: 0.92rem;

  @media (max-width: 900px) {
    white-space: normal;
    font-size: 0.86rem;
    padding: 0.52rem 0.68rem;
    border: 1px solid rgba(148, 163, 184, 0.24);
    border-radius: 0.62rem;
    width: 100%;
  }

  &.active {
    color: #fff;
    background: rgba(102, 126, 234, 0.35);
  }

  &:hover {
    color: #fff;
  }
`;

const NoticeGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 2.1rem;
  padding: 0 0.2rem;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    height: 0.5rem;
  }

  &:hover > div,
  &:focus-within > div {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    pointer-events: auto;
  }

  @media (max-width: 900px) {
    height: auto;
    padding: 0;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
`;

const NoticeLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: ${({ $active }) => ($active ? '#fff' : '#cbd5e1')};
  background: ${({ $active }) => ($active ? 'rgba(102, 126, 234, 0.35)' : 'transparent')};
  border-radius: 999px;
  padding: 0.36rem 0.72rem;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: default;

  @media (max-width: 900px) {
    display: none;
  }
`;
const NoticeMenuLink = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #cbd5e1;
  background: transparent;
  border-radius: 999px;
  padding: 0.36rem 0.72rem;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;

  @media (max-width: 900px) {
    display: none;
  }

  &.active {
    color: #fff;
    background: rgba(102, 126, 234, 0.35);
  }
`;

const NoticeSubMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.3rem);
  left: 50%;
  transform: translate(-50%, 4px);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  min-width: 140px;
  padding: 0.5rem;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 0.75rem;
  background: rgba(15, 23, 42, 0.98);
  box-shadow: 0 10px 30px rgba(2, 6, 23, 0.5);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease;
  z-index: 10;

  @media (max-width: 900px) {
    position: static;
    transform: none;
    opacity: ${({ $mobileOpen }) => ($mobileOpen ? 1 : 0)};
    visibility: ${({ $mobileOpen }) => ($mobileOpen ? 'visible' : 'hidden')};
    pointer-events: ${({ $mobileOpen }) => ($mobileOpen ? 'auto' : 'none')};
    display: ${({ $mobileOpen }) => ($mobileOpen ? 'flex' : 'none')};
    box-shadow: none;
    border: none;
    background: transparent;
    padding: 0.2rem 0 0 0.55rem;
    min-width: 0;
    width: 100%;
  }
`;

const NoticeSubLink = styled(NavLink)`
  display: block;
  width: 100%;
  padding: 0.42rem 0.72rem;
  border-radius: 0.55rem;
  text-align: left;
  color: #cbd5e1;
  font-size: 0.86rem;
  border: 1px solid rgba(148, 163, 184, 0.28);

  &.active {
    color: #fff;
    background: rgba(102, 126, 234, 0.35);
    border-color: transparent;
  }

  &:hover {
    color: #fff;
  }

  @media (max-width: 900px) {
    border: 1px solid rgba(148, 163, 184, 0.24);
    width: 100%;
    padding: 0.52rem 0.68rem;
    font-size: 0.84rem;
  }
`;

const PresentationGroup = styled(NoticeGroup)``;

const PresentationMenuLink = styled(NoticeMenuLink)``;

const PresentationSubMenu = styled(NoticeSubMenu)``;

const PresentationSubLink = styled(NoticeSubLink)``;

const MenuGroupToggle = styled.button`
  display: none;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(148, 163, 184, 0.24)')};
  background: ${({ $active }) => ($active ? 'rgba(99, 102, 241, 0.16)' : 'rgba(15, 23, 42, 0.2)')};
  color: #e2e8f0;
  border-radius: 0.62rem;
  width: 100%;
  min-height: 2.15rem;
  padding: 0.45rem 0.65rem;
  font-size: 0.86rem;
  font-weight: 700;
  text-align: left;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  @media (max-width: 900px) {
    display: inline-flex;
  }
`;

const ToggleChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.18s ease;
`;

const ServiceGroup = styled(NoticeGroup)``;

const ServiceLabel = styled(NoticeLabel)``;

const ServiceSubMenu = styled(NoticeSubMenu)``;

const ServiceSubLink = styled(NoticeSubLink)``;

const TeacherManageGroup = styled(NoticeGroup)``;

const TeacherManageLabel = styled(NoticeLabel)``;

const TeacherManageSubMenu = styled(NoticeSubMenu)``;

const TeacherManageSubLink = styled(NoticeSubLink)``;

const TypingGroup = styled(NoticeGroup)``;

const TypingLabel = styled(NoticeLabel)``;

const TypingSubMenu = styled(NoticeSubMenu)``;

const TypingSubLink = styled(NoticeSubLink)``;

const StudentManageGroup = styled(NoticeGroup)``;

const StudentManageLabel = styled(NoticeLabel)``;

const StudentManageSubMenu = styled(NoticeSubMenu)``;

const StudentManageSubLink = styled(NoticeSubLink)``;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  justify-content: flex-end;
  min-width: 220px;

  @media (max-width: 900px) {
    min-width: 0;
    margin-left: auto;
    gap: 0.4rem;
  }
`;

const RoleBadge = styled.span`
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  color: #cbd5e1;
  border: 1px solid rgba(148, 163, 184, 0.35);
  font-size: 0.82rem;
  line-height: 1;

  @media (max-width: 900px) {
    display: none;
  }
`;

const LoginLink = styled(NavLink)`
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  color: #cbd5e1;
  border: 1px solid rgba(148, 163, 184, 0.35);
  font-size: 0.92rem;
  font-weight: 500;
`;

const ActionButton = styled.button`
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: transparent;
  color: #cbd5e1;
  font-size: 0.92rem;
  cursor: pointer;

  &:hover {
    color: #fff;
  }
`;

const ProfileMenuWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(99, 102, 241, 0.65)' : 'transparent')};
  background: ${({ $active }) => ($active ? 'rgba(99, 102, 241, 0.14)' : 'transparent')};
`;

const UserDisplayName = styled.span`
  color: #cbd5e1;
  font-size: 0.9rem;
  font-weight: 600;

  @media (max-width: 900px) {
    display: none;
  }
`;

const AvatarButton = styled.button`
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.6);
  color: #e2e8f0;
  font-size: 1.15rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ProfileDropdown = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 0.5rem);
  min-width: 150px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.5rem;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 0.75rem;
  background: rgba(15, 23, 42, 0.98);
  box-shadow: 0 10px 30px rgba(2, 6, 23, 0.5);
  z-index: 20;
`;

const MenuLink = styled(NavLink)`
  padding: 0.45rem 0.65rem;
  border-radius: 0.55rem;
  color: #cbd5e1;
  font-size: 0.88rem;

  &.active {
    color: #fff;
    background: rgba(102, 126, 234, 0.35);
  }
`;

const PORTAL_USER_KEY = 'portal_user';
const PORTAL_USER_UPDATED_EVENT = 'portal_user_updated';

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.roles) ? parsed : null;
  } catch {
    return null;
  }
};

const toRoleTokens = (roles = []) =>
  roles
    .map((role) => {
      if (typeof role === 'string') return role.trim().toLowerCase();
      if (role && typeof role === 'object') {
        const code = String(
          role.code || role.role || role.name || role.role_name || role.roleName || role.value || ''
        )
          .trim()
          .toLowerCase();
        return code;
      }
      return '';
    })
    .filter(Boolean);

const hasAnyRole = (roles = [], candidates = []) => {
  const roleTokens = toRoleTokens(roles);
  return candidates.some((candidate) => roleTokens.includes(candidate));
};

const resolvePortalHomePath = (roles = []) => {
  const roleTokens = toRoleTokens(roles);
  if (
    roleTokens.includes('super_admin') ||
    roleTokens.includes('admin') ||
    roleTokens.includes('teacher') ||
    roleTokens.includes('student') ||
    roleTokens.includes('parent') ||
    roleTokens.includes('관리자') ||
    roleTokens.includes('최고관리자')
  ) {
    return '/portal/home';
  }
  return '/portal/login';
};

const resolveRoleLabel = (roles = []) => {
  const roleTokens = toRoleTokens(roles);
  if (
    roleTokens.includes('super_admin') ||
    roleTokens.includes('admin') ||
    roleTokens.includes('관리자') ||
    roleTokens.includes('최고관리자')
  ) {
    return '관리자';
  }
  if (hasAnyRole(roles, ['teacher', 'instructor', 'tutor', '강사', '교사'])) return '강사';
  if (hasAnyRole(roles, ['student', '학생'])) return '학생';
  if (hasAnyRole(roles, ['parent', '학부모'])) return '학부모';
  return '포털';
};

const resolveUserRoleSuffix = (roles = []) => {
  if (
    hasAnyRole(roles, ['super_admin', 'admin', '관리자', '최고관리자'])
  ) {
    return '관리자님';
  }
  if (hasAnyRole(roles, ['teacher', 'instructor', 'tutor', '강사', '교사'])) return 'T';
  if (hasAnyRole(roles, ['parent', '학부모'])) return '학부모님';
  if (hasAnyRole(roles, ['student', '학생'])) return '학생';
  return '사용자';
};

const isAdminRoles = (roles = []) => {
  const roleTokens = toRoleTokens(roles);
  return (
    roleTokens.includes('admin') ||
    roleTokens.includes('super_admin') ||
    roleTokens.includes('관리자') ||
    roleTokens.includes('최고관리자') ||
    roleTokens.includes('staff')
  );
};

const PortalNavBar = () => {
  const history = useHistory();
  const location = useLocation();
  const [portalUser, setPortalUser] = useState(() => readPortalUser());
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const menuRef = useRef(null);
  const clearPortalSession = useCallback(
    (shouldRedirect = false) => {
      sessionStorage.removeItem(PORTAL_USER_KEY);
      window.dispatchEvent(new Event(PORTAL_USER_UPDATED_EVENT));
      setPortalUser(null);
      if (shouldRedirect) {
        history.replace('/portal/login');
      }
    },
    [history]
  );

  useEffect(() => {
    setPortalUser(readPortalUser());
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
    setMobileExpanded(defaultMobileExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const syncPortalUser = () => setPortalUser(readPortalUser());
    window.addEventListener(PORTAL_USER_UPDATED_EVENT, syncPortalUser);
    return () => window.removeEventListener(PORTAL_USER_UPDATED_EVENT, syncPortalUser);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);
  const roles = portalUser?.roles || [];
  const portalHomePath = resolvePortalHomePath(roles);
  const roleLabel = resolveRoleLabel(roles);
  const profileEmoji = portalUser?.profile_emoji || '🙂';
  const displayName = portalUser?.name || portalUser?.username || portalUser?.login_id || '사용자';
  const userRoleSuffix = resolveUserRoleSuffix(roles);
  const showPortalServiceMenu =
    isAdminRoles(roles) || roleLabel === '관리자' || location.pathname.startsWith('/portal/staff');
  const isTypingActive =
    location.pathname.startsWith('/portal/typing') || location.pathname.startsWith('/portal/teacher/typing');
  const isServiceActive = location.pathname === '/portal/staff';
  const isTeacherManageActive = location.pathname.startsWith('/portal/staff/teachers');
  const showStudentTypingMenu = hasAnyRole(roles, ['student', '학생']);
  const showTeacherTypingMenu = hasAnyRole(roles, ['teacher', 'instructor', 'tutor', '강사', '교사']);
  const isStudentManageActive = location.pathname.startsWith('/portal/teacher/attendance-records');
  const isBoardActive =
    location.pathname.startsWith('/portal/notices') || location.pathname.startsWith('/portal/board');
  const isPresentationActive = location.pathname.startsWith('/portal/presentations');
  const defaultMobileExpanded = {
    presentation: isPresentationActive,
    board: isBoardActive,
    typingStudent: showStudentTypingMenu && isTypingActive,
    typingTeacher: showTeacherTypingMenu && isTypingActive,
    studentManage: showTeacherTypingMenu && isStudentManageActive,
    service: showPortalServiceMenu && isServiceActive,
    teacherManage: showPortalServiceMenu && isTeacherManageActive
  };

  const handleLogout = () => {
    clearPortalSession(true);
  };

  const toggleMobileGroup = (key) => {
    setMobileExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCenterNavClick = (event) => {
    if (window.innerWidth > 900) return;
    if (event.target.closest('a')) {
      setIsMobileMenuOpen(false);
      setMobileExpanded(defaultMobileExpanded);
    }
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen((prev) => {
      const next = !prev;
      if (next) setMobileExpanded(defaultMobileExpanded);
      return next;
    });
  };

  return (
    <Header>
      <Inner>
        <LeftGroup>
          <Brand to={portalHomePath}>AceCoding Portal</Brand>
          {portalUser && <RoleBadge>{roleLabel}</RoleBadge>}
        </LeftGroup>
        {portalUser && (
          <MobileMenuButton
            type="button"
            onClick={handleMobileMenuToggle}
            aria-label={isMobileMenuOpen ? '메뉴 닫기' : '메뉴 열기'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </MobileMenuButton>
        )}
        {portalUser ? (
          <CenterNav $open={isMobileMenuOpen} onClick={handleCenterNavClick}>
            <CenterLink to="/portal/home">홈</CenterLink>
            <PresentationGroup>
              <PresentationMenuLink
                to="/portal/presentations/year/2026"
                isActive={(_, nextLocation) =>
                  (nextLocation?.pathname || '').startsWith('/portal/presentations')
                }
              >
                발표회 <span aria-hidden="true">▾</span>
              </PresentationMenuLink>
              <MenuGroupToggle
                type="button"
                $active={isPresentationActive}
                onClick={() => toggleMobileGroup('presentation')}
                aria-expanded={Boolean(mobileExpanded.presentation)}
              >
                발표회
                <ToggleChevron $open={Boolean(mobileExpanded.presentation)} aria-hidden="true">▾</ToggleChevron>
              </MenuGroupToggle>
              <PresentationSubMenu $mobileOpen={Boolean(mobileExpanded.presentation)}>
                <PresentationSubLink
                  to="/portal/presentations/year/2025"
                  isActive={(_, nextLocation) => {
                    const pathname = nextLocation?.pathname || '';
                    return pathname.includes('/portal/presentations/year/2025');
                  }}
                >
                  2025 크리스마스 발표회
                </PresentationSubLink>
                <PresentationSubLink
                  to="/portal/presentations/year/2026"
                  isActive={(_, nextLocation) => {
                    const pathname = nextLocation?.pathname || '';
                    return pathname.includes('/portal/presentations/year/2026');
                  }}
                >
                  2026 어린이날 발표회
                </PresentationSubLink>
              </PresentationSubMenu>
            </PresentationGroup>
            <NoticeGroup>
              <NoticeMenuLink
                to="/portal/notices"
                isActive={(_, nextLocation) =>
                  (nextLocation?.pathname || '').startsWith('/portal/notices') ||
                  (nextLocation?.pathname || '').startsWith('/portal/board')
                }
              >
                게시판 <span aria-hidden="true">▾</span>
              </NoticeMenuLink>
              <MenuGroupToggle
                type="button"
                $active={isBoardActive}
                onClick={() => toggleMobileGroup('board')}
                aria-expanded={Boolean(mobileExpanded.board)}
              >
                게시판
                <ToggleChevron $open={Boolean(mobileExpanded.board)} aria-hidden="true">▾</ToggleChevron>
              </MenuGroupToggle>
              <NoticeSubMenu $mobileOpen={Boolean(mobileExpanded.board)}>
                <NoticeSubLink to="/portal/notices">공지사항</NoticeSubLink>
                <NoticeSubLink to="/portal/board">학생 자유 게시판</NoticeSubLink>
              </NoticeSubMenu>
            </NoticeGroup>
            {showStudentTypingMenu && (
              <TypingGroup>
                <TypingLabel $active={isTypingActive}>
                  타자 <span aria-hidden="true">▾</span>
                </TypingLabel>
                <MenuGroupToggle
                  type="button"
                  $active={isTypingActive}
                  onClick={() => toggleMobileGroup('typingStudent')}
                  aria-expanded={Boolean(mobileExpanded.typingStudent)}
                >
                  타자
                  <ToggleChevron $open={Boolean(mobileExpanded.typingStudent)} aria-hidden="true">▾</ToggleChevron>
                </MenuGroupToggle>
                <TypingSubMenu $mobileOpen={Boolean(mobileExpanded.typingStudent)}>
                  <TypingSubLink to="/portal/typing/all">전체 타자 기록</TypingSubLink>
                  <TypingSubLink to="/portal/typing/my">내 타자 기록</TypingSubLink>
                  <TypingSubLink to="/portal/typing/today">오늘의 타자 기록</TypingSubLink>
                </TypingSubMenu>
              </TypingGroup>
            )}
            {showTeacherTypingMenu && (
              <TypingGroup>
                <TypingLabel $active={isTypingActive}>
                  타자 <span aria-hidden="true">▾</span>
                </TypingLabel>
                <MenuGroupToggle
                  type="button"
                  $active={isTypingActive}
                  onClick={() => toggleMobileGroup('typingTeacher')}
                  aria-expanded={Boolean(mobileExpanded.typingTeacher)}
                >
                  타자
                  <ToggleChevron $open={Boolean(mobileExpanded.typingTeacher)} aria-hidden="true">▾</ToggleChevron>
                </MenuGroupToggle>
                <TypingSubMenu $mobileOpen={Boolean(mobileExpanded.typingTeacher)}>
                  <TypingSubLink to="/portal/teacher/typing-approvals">타자 기록 승인 요청</TypingSubLink>
                </TypingSubMenu>
              </TypingGroup>
            )}
            {showTeacherTypingMenu && (
              <StudentManageGroup>
                <StudentManageLabel $active={isStudentManageActive}>
                  학생관리 <span aria-hidden="true">▾</span>
                </StudentManageLabel>
                <MenuGroupToggle
                  type="button"
                  $active={isStudentManageActive}
                  onClick={() => toggleMobileGroup('studentManage')}
                  aria-expanded={Boolean(mobileExpanded.studentManage)}
                >
                  학생관리
                  <ToggleChevron $open={Boolean(mobileExpanded.studentManage)} aria-hidden="true">▾</ToggleChevron>
                </MenuGroupToggle>
                <StudentManageSubMenu $mobileOpen={Boolean(mobileExpanded.studentManage)}>
                  <StudentManageSubLink to="/portal/teacher/attendance-records">
                    입실기록 관리
                  </StudentManageSubLink>
                </StudentManageSubMenu>
              </StudentManageGroup>
            )}
            {showPortalServiceMenu && (
              <>
                <ServiceGroup>
                  <ServiceLabel $active={isServiceActive}>
                    포털 서비스 <span aria-hidden="true">▾</span>
                  </ServiceLabel>
                  <MenuGroupToggle
                    type="button"
                    $active={isServiceActive}
                    onClick={() => toggleMobileGroup('service')}
                    aria-expanded={Boolean(mobileExpanded.service)}
                  >
                    포털 서비스
                    <ToggleChevron $open={Boolean(mobileExpanded.service)} aria-hidden="true">▾</ToggleChevron>
                  </MenuGroupToggle>
                  <ServiceSubMenu $mobileOpen={Boolean(mobileExpanded.service)}>
                    <ServiceSubLink exact to="/portal/staff">오늘의 메세지 관리</ServiceSubLink>
                  </ServiceSubMenu>
                </ServiceGroup>
                <TeacherManageGroup>
                  <TeacherManageLabel $active={isTeacherManageActive}>
                    선생님 관리 <span aria-hidden="true">▾</span>
                  </TeacherManageLabel>
                  <MenuGroupToggle
                    type="button"
                    $active={isTeacherManageActive}
                    onClick={() => toggleMobileGroup('teacherManage')}
                    aria-expanded={Boolean(mobileExpanded.teacherManage)}
                  >
                    선생님 관리
                    <ToggleChevron $open={Boolean(mobileExpanded.teacherManage)} aria-hidden="true">▾</ToggleChevron>
                  </MenuGroupToggle>
                  <TeacherManageSubMenu $mobileOpen={Boolean(mobileExpanded.teacherManage)}>
                    <TeacherManageSubLink to="/portal/staff/teachers">선생님 정보 관리</TeacherManageSubLink>
                  </TeacherManageSubMenu>
                </TeacherManageGroup>
              </>
            )}
          </CenterNav>
        ) : (
          <CenterNav $open={false} />
        )}
        <RightActions>
          {portalUser ? (
            <ProfileMenuWrap ref={menuRef} $active={Boolean(portalUser)}>
              <UserDisplayName>{`${displayName} ${userRoleSuffix}`}</UserDisplayName>
              <AvatarButton
                type="button"
                onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                aria-label="프로필 메뉴 열기"
              >
                {profileEmoji}
              </AvatarButton>
              {isProfileMenuOpen && (
                <ProfileDropdown>
                  <MenuLink to="/portal/profile" onClick={() => setIsProfileMenuOpen(false)}>
                    프로필 설정
                  </MenuLink>
                  <ActionButton type="button" onClick={handleLogout}>
                    로그아웃
                  </ActionButton>
                </ProfileDropdown>
              )}
            </ProfileMenuWrap>
          ) : (
            <LoginLink to="/portal/login">로그인</LoginLink>
          )}
        </RightActions>
      </Inner>
    </Header>
  );
};

export default PortalNavBar;
